/**
 * Error Boundary for AI Preview Component
 * Provides graceful error handling and fallback UI
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@ui-elements';
import { AIPreviewError } from './types';
import styles from './index.module.scss';

interface Props {
  children: ReactNode;
  onError?: (error: AIPreviewError, errorInfo: ErrorInfo) => void;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string;
}

export class AIPreviewErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: '',
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorId:
        typeof window !== 'undefined'
          ? `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          : 'ssr-error',
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Generate errorId on client if not already set
    const errorId =
      this.state.errorId ||
      (typeof window !== 'undefined'
        ? `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        : 'ssr-error');

    if (!this.state.errorId) {
      this.setState({ errorId });
    }

    // Convert to AIPreviewError format
    const aiError: AIPreviewError = {
      type: 'component',
      message: 'AI Preview component crashed',
      details: `${error.message}\n\nComponent Stack:\n${errorInfo.componentStack}`,
      isRetryable: true,
      timestamp: new Date(),
      errorId,
    };

    // Call error handler if provided
    this.props.onError?.(aiError, errorInfo);

    // Log to console for debugging
    console.error('AI Preview Error Boundary caught an error:', {
      error,
      errorInfo,
      aiError,
    });

    // Send to error reporting service if available
    if (typeof window !== 'undefined' && (window as any).errorReporting) {
      (window as any).errorReporting.captureException(error, {
        tags: {
          component: 'AIPreview',
          errorBoundary: true,
        },
        extra: {
          componentStack: errorInfo.componentStack,
          errorId,
        },
      });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorId:
        typeof window !== 'undefined'
          ? `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          : 'ssr-error',
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div
          className={`${styles.aiPreviewContainer} ${styles.errorState}`}
          role="alert"
          aria-live="assertive"
          data-testid="ai-preview-error-boundary"
        >
          <div className={styles.errorMessage}>
            AI Preview temporarily unavailable
          </div>

          <div
            style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '12px',
              lineHeight: '1.4',
            }}
          >
            We&apos;re having trouble loading the AI insights for your search.
            You can still view your search results below.
          </div>

          <Button
            type="secondary"
            buttonSize="medium"
            className={styles.retryButton}
            onClick={this.handleRetry}
            aria-describedby="retry-description"
          >
            Try Again
          </Button>

          {/* Hidden description for screen readers */}
          <div id="retry-description" className={styles.srOnly}>
            Retry loading the AI preview component
          </div>

          {/* Error details for debugging (only in development) */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details
              style={{
                marginTop: '16px',
                fontSize: '12px',
                color: '#6b7280',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                padding: '8px',
              }}
            >
              <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
                Development Error Details (ID: {this.state.errorId})
              </summary>
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  fontSize: '11px',
                  backgroundColor: '#f9fafb',
                  padding: '8px',
                  borderRadius: '3px',
                  overflow: 'auto',
                  maxHeight: '200px',
                }}
              >
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default AIPreviewErrorBoundary;
